import { useId, useState, type FormEvent } from "react";
import { useAuth } from "@workos-inc/authkit-react";
import { useAction, useConvex } from "convex/react";
import { Building2, Loader2, LogOut } from "lucide-react";
import { api } from "@constructionautomation/backend";
import { ORGANIZATION_NAME_MAX_LENGTH } from "@constructionautomation/backend/types/signup";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { currentReturnTo } from "@/lib/auth-return-to";
import { enterOrganization } from "@/lib/enter-organization";
import { usePageRestore } from "@/lib/use-page-restore";
import { captureUserActionError } from "@/lib/sentry";
import { getSignupError, getSignupErrorMessage } from "@/lib/signup-errors";

interface OrganizationOnboardingScreenProps {
  email: string | null;
}

/**
 * Where a new company creator lands: subscription selection when the company has no paid plan or
 * internal grant yet (CON-426 effective access), otherwise the page they were heading to.
 */
async function resolvePostSetupDestination(convex: ReturnType<typeof useConvex>) {
  const returnTo = currentReturnTo() ?? "/";
  try {
    const overview = await convex.query(api.billing.queries.getOrganizationBillingOverview, {});
    if (overview.canManageBilling && overview.summary.state === "unpaid_setup") {
      return "/organization";
    }
  } catch (error) {
    captureUserActionError(error, "auth.postSetupDestination");
  }
  return returnTo;
}

export function OrganizationOnboardingScreen({ email }: OrganizationOnboardingScreenProps) {
  const auth = useAuth();
  const convex = useConvex();
  const createOrganization = useAction(api.signup.createOrganization);
  const syncIdentity = useAction(api.authActions.syncIdentityFromWorkos);
  const nameId = useId();
  const errorId = useId();

  const [orgName, setOrgName] = useState("");
  const [pending, setPending] = useState<"create" | "refresh" | "signOut" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasPendingInvitation, setHasPendingInvitation] = useState(false);
  // Entering the new company leaves the page with `pending` set; Back restores it from bfcache.
  usePageRestore(() => setPending(null));

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = orgName.trim();
    if (!trimmed) {
      setError("Enter your company name.");
      return;
    }

    setPending("create");
    setError(null);
    try {
      // Safe to repeat: the server resumes an interrupted creation instead of duplicating it.
      const result = await createOrganization({ name: trimmed });
      // The membership is saved locally, so access state resolves before the session switch.
      const destination = await resolvePostSetupDestination(convex);
      await enterOrganization(auth, result.workosOrganizationId, destination);
    } catch (err) {
      captureUserActionError(err, "auth.createOrganization");
      setHasPendingInvitation(getSignupError(err)?.code === "INVITATION_PENDING");
      setError(getSignupErrorMessage(err, "We couldn't set up your company. Try again."));
      setPending(null);
    }
  };

  const handleRefreshAccess = async () => {
    setPending("refresh");
    setError(null);
    try {
      const result = await syncIdentity({});
      if (!result.requiresOnboarding) {
        window.location.reload();
        return;
      }
      setError("No company membership was found yet. If you were invited, open the link in your invitation email.");
    } catch (err) {
      setError(getSignupErrorMessage(err, "We couldn't check your company access."));
    } finally {
      setPending((current) => (current === "refresh" ? null : current));
    }
  };

  const handleSignOut = async () => {
    setPending("signOut");
    await auth.signOut({ navigate: false });
    window.location.reload();
  };

  const isBusy = pending !== null;

  return (
    <main className="flex min-h-dvh items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md py-4">
        <CardHeader className="pb-2 text-center">
          <div className="mx-auto mb-2 flex size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Building2 className="size-6" aria-hidden="true" />
          </div>
          <CardTitle className="text-lg tracking-tight">
            <h1>Set Up Your Company</h1>
          </CardTitle>
          <CardDescription>
            Create your company workspace. Next, you'll start a subscription so your team can create and process work.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form className="space-y-3" onSubmit={(event) => void handleCreate(event)} noValidate>
            <div className="space-y-2">
              <Label htmlFor={nameId}>Company name</Label>
              <Input
                id={nameId}
                name="organization"
                autoComplete="organization"
                value={orgName}
                onChange={(event) => setOrgName(event.target.value)}
                maxLength={ORGANIZATION_NAME_MAX_LENGTH}
                required
                disabled={isBusy}
                aria-invalid={error !== null && !hasPendingInvitation}
                aria-describedby={error ? errorId : undefined}
                autoFocus
              />
            </div>
            {error ? (
              <Alert variant={hasPendingInvitation ? "warning" : "destructive"} id={errorId}>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <Button type="submit" className="w-full" disabled={isBusy}>
              {pending === "create" ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  Setting Up...
                </>
              ) : (
                "Create Company"
              )}
            </Button>
          </form>

          <div className="space-y-2 border-t-2 border-border pt-4">
            {email ? (
              <p className="text-center text-sm text-muted-foreground">
                Signed in as <span className="font-semibold text-foreground">{email}</span>
              </p>
            ) : null}
            <p className="text-center text-sm text-muted-foreground">
              Joining an existing company? Open the link in your invitation email instead.
            </p>
            <Button
              className="w-full"
              variant="outline"
              onClick={() => void handleRefreshAccess()}
              disabled={isBusy}
            >
              {pending === "refresh" ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
              {pending === "refresh" ? "Checking..." : "Check Company Access Again"}
            </Button>
            <Button className="w-full" variant="ghost" onClick={() => void handleSignOut()} disabled={isBusy}>
              <LogOut className="size-4" aria-hidden="true" />
              Use a Different Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
